<script>
import { useExamDataStore } from "@/stores/exam/exam-data-store";
import { computed } from "vue";

export default {
  name: "ChoiceExam",
  props: {
    quiz: {
      type: Object,
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    isReview: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const examStore = useExamDataStore();

    const studentAnswer = computed(() => {
      if (props.isReview) {
        return (props.quiz?.answer || "").trim().toUpperCase();
      }
      return null;
    });

    const correctAnswer = computed(() => {
      return (props.quiz?.correctAnswer || "").trim().toUpperCase();
    });

    const hasStudentAnswer = computed(() => {
      return Boolean(studentAnswer.value);
    });

    const isStudentCorrect = computed(() => {
      if (props.quiz?.is_correct !== undefined && props.quiz?.is_correct !== null) {
        return Boolean(props.quiz.is_correct);
      }
      return Boolean(
        hasStudentAnswer.value && studentAnswer.value === correctAnswer.value
      );
    });

    const currentAnswer = computed({
      get() {
        if (props.isReview) return studentAnswer.value;
        if (props.preview) return null;
        const item = examStore.examAnswer?.find(
          (a) =>
            String(a.question) ===
            String(props.quiz?.questionID || props.quiz?.question)
        );
        return item ? item.answer : null;
      },
      set(answer) {
        if (props.preview || props.isReview) return;
        examStore.setAnswer(
          props.quiz?.questionID || props.quiz?.question,
          answer
        );
      },
    });

    const getOptionClass = (key) => {
      if (!props.isReview) {
        return {
          "correct-answer": props.preview && correctAnswer.value === key,
        };
      }

      const isChosen = studentAnswer.value === key;
      const isKey = correctAnswer.value === key;

      if (isChosen && isStudentCorrect.value) {
        return "option-student-correct";
      }
      if (isChosen && !isStudentCorrect.value) {
        return "option-student-wrong";
      }
      if (isKey && !isStudentCorrect.value) {
        return "option-key-correct";
      }
      return "";
    };

    return {
      examStore,
      currentAnswer,
      studentAnswer,
      correctAnswer,
      hasStudentAnswer,
      isStudentCorrect,
      getOptionClass,
    };
  },
};
</script>

<template>
  <div class="w-100 w-md-75 p-4">
    <div class="question-container mb-4">
      <!-- Status Box khusus Review / Grading Guru -->
      <div
        v-if="isReview"
        class="alert d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 mb-4 rounded-3"
        :class="
          isStudentCorrect
            ? 'alert-success border-success-subtle bg-success-subtle text-success-emphasis'
            : hasStudentAnswer
            ? 'alert-danger border-danger-subtle bg-danger-subtle text-danger-emphasis'
            : 'alert-secondary border-secondary-subtle bg-light text-secondary'
        "
      >
        <div class="d-flex align-items-center gap-2">
          <span class="badge text-bg-light border fw-semibold">Pilihan Ganda</span>
          <span class="small fw-medium">
            <span v-if="!hasStudentAnswer">Siswa tidak menjawab soal ini.</span>
            <span v-else-if="isStudentCorrect">
              Jawaban siswa benar ({{ studentAnswer }}).
            </span>
            <span v-else>
              Jawaban siswa: <strong>{{ studentAnswer }}</strong> (Salah). Kunci jawaban:
              <strong>{{ correctAnswer }}</strong>.
            </span>
          </span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span
            class="badge rounded-pill"
            :class="
              isStudentCorrect
                ? 'text-bg-success'
                : hasStudentAnswer
                ? 'text-bg-danger'
                : 'text-bg-secondary'
            "
          >
            {{ isStudentCorrect ? "Benar (+100)" : "Salah (0)" }}
          </span>
          <span class="badge text-bg-secondary opacity-75">
            Read-only (Dinilai Otomatis)
          </span>
        </div>
      </div>

      <form>
        <h4 class="question-text mb-4">{{ quiz?.questionText }}</h4>

        <div
          class="option-list"
          :class="{
            'is-preview': preview || isReview,
            'is-review-mode': isReview,
          }"
        >
          <div
            v-for="opt in [
              { key: 'A', text: quiz?.option_a },
              { key: 'B', text: quiz?.option_b },
              { key: 'C', text: quiz?.option_c },
              { key: 'D', text: quiz?.option_d },
            ]"
            :key="opt.key"
            class="option-item"
          >
            <input
              type="radio"
              :name="'option-' + (quiz.questionID || quiz.question)"
              :id="'option-' + (quiz.questionID || quiz.question) + '-' + opt.key"
              class="option-input"
              :value="opt.key"
              v-model="currentAnswer"
              :disabled="preview || isReview"
            />
            <label
              :for="'option-' + (quiz.questionID || quiz.question) + '-' + opt.key"
              class="option-label"
              :class="getOptionClass(opt.key)"
            >
              <span class="option-key-pill me-2">{{ opt.key }}.</span>
              <span class="option-text flex-grow-1">{{ opt.text }}</span>

              <!-- Badge indikator di samping opsi untuk Review Guru -->
              <template v-if="isReview">
                <span
                  v-if="opt.key === studentAnswer && isStudentCorrect"
                  class="badge bg-success ms-2 py-1 px-2 d-inline-flex align-items-center gap-1"
                >
                  <i class="isax isax-tick-circle"></i> Jawaban Siswa
                </span>
                <span
                  v-else-if="opt.key === studentAnswer && !isStudentCorrect"
                  class="badge bg-danger ms-2 py-1 px-2 d-inline-flex align-items-center gap-1"
                >
                  <i class="isax isax-close-circle"></i> Jawaban Siswa
                </span>
                <span
                  v-else-if="opt.key === correctAnswer && !isStudentCorrect"
                  class="badge bg-success-subtle text-success border border-success ms-2 py-1 px-2 d-inline-flex align-items-center gap-1"
                >
                  <i class="isax isax-tick-circle"></i> Kunci Jawaban
                </span>
              </template>
            </label>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.correct-answer {
  background-color: #d1fae5 !important; /* hijau muda */
  border: 1px solid #10b981 !important; /* hijau */
  color: #065f46 !important;
  font-weight: 600;
}

.option-student-correct {
  background-color: #e8f5e9 !important;
  border: 1.5px solid #2e7d32 !important;
  color: #1b5e20 !important;
  font-weight: 600;
}

.option-student-wrong {
  background-color: #ffebee !important;
  border: 1.5px solid #c62828 !important;
  color: #b71c1c !important;
  font-weight: 600;
}

.option-key-correct {
  background-color: #f1f8e9 !important;
  border: 1.5px dashed #43a047 !important;
  color: #2e7d32 !important;
  font-weight: 600;
}

.bg-main {
  background-color: #ff4667;
}

.color-main {
  color: #ff4667;
}

/* Question Container */
.question-container {
  margin: 0 auto;
}

/* Question Text */
.question-text {
  font-size: 1.25rem;
  font-weight: 500;
  color: #2d3748;
  line-height: 1.4;
}

/* Option List */
.option-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Option Item */
.option-item {
  display: flex;
  align-items: center;
}

/* Option Input (Radio Button) */
.option-input {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  accent-color: #ff4667;
  cursor: pointer;
}

/* Option Label */
.option-label {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 100%;
}

.option-key-pill {
  font-weight: 700;
  color: #64748b;
}

.option-label:hover {
  background-color: #f1f5f9;
}

.option-input:checked + .option-label:not(.option-student-wrong):not(.option-student-correct) {
  background-color: #ffdbe2;
  box-shadow: 0 0 0 1px #ff4667;
}

.option-input:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.is-preview .option-label {
  cursor: default;
}

.is-preview .option-label:hover:not(.option-student-wrong):not(.option-student-correct):not(.option-key-correct) {
  background-color: #f8fafc;
}

/* Option Text */
.option-text {
  font-size: 1rem;
  color: #334155;
}
</style>
